import React, { useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Button, DatePicker, Form, Modal, Select, notification } from "antd";
import { api } from "../utils/Api";

const range = (start, end) =>
	Array.from({ length: Math.max(end - start, 0) }, (_, i) => start + i);

// Days before today are not selectable
const disabledDate = (current) => current && current < dayjs().startOf("day");

// On today's date, hours and minutes already past are not selectable
const disabledTime = (current) => {
	const now = dayjs();
	if (!current || !current.isSame(now, "day")) {
		return {};
	}
	return {
		disabledHours: () => range(0, now.hour()),
		disabledMinutes: (hour) =>
			hour === now.hour() ? range(0, now.minute()) : [],
	};
};

const filterOption = (input, option) =>
	(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

const DriverShiftNewModal = ({
	open,
	onClose,
	onSuccess,
	plantOptions,
	loadingPlants,
	defaultPlant,
}) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [apiNotification, contextHolder] = notification.useNotification();

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	const onFinish = async (values) => {
		const url = api + "/shift/create";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoading(true);
			const { data } = await axios.post(url, {
				date: values.date.toDate(),
				plant: values.plant,
				timezoneOffset: values.date.toDate().getTimezoneOffset(),
			});
			if (data.success) {
				form.resetFields();
				onSuccess(data.message);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to create shift");
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			title="Create New Shift"
			open={open}
			onCancel={onClose}
			centered
			destroyOnClose
			maskClosable={false}
			footer={[
				<Button key="cancel" size="large" onClick={onClose} disabled={loading}>
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					size="large"
					loading={loading}
					onClick={() => form.submit()}
				>
					Save
				</Button>,
			]}
		>
			{contextHolder}
			<Form
				form={form}
				layout="vertical"
				style={{ marginTop: "20px" }}
				onFinish={onFinish}
				preserve={false}
			>
				<Form.Item
					name="date"
					label="Date"
					rules={[
						{ required: true, message: "Date is required" },
						{
							validator: (_, value) =>
								!value || !value.isBefore(dayjs(), "minute")
									? Promise.resolve()
									: Promise.reject(
											new Error("Date can't be earlier than the current time"),
									  ),
						},
					]}
				>
					<DatePicker
						size="large"
						showTime={{ format: "hh:mm A" }}
						format="MM/DD/YYYY hh:mm A"
						disabledDate={disabledDate}
						disabledTime={disabledTime}
						style={{ width: "100%" }}
					/>
				</Form.Item>
				<Form.Item
					name="plant"
					label="Plant"
					initialValue={defaultPlant ?? undefined}
					rules={[{ required: true, message: "Plant is required" }]}
				>
					<Select
						showSearch
						placeholder="Select plant"
						size="large"
						options={plantOptions}
						filterOption={filterOption}
						loading={loadingPlants}
						disabled={loadingPlants}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default DriverShiftNewModal;
